import { UpgradeSubscriptionModal } from '@components'

import {
  DropdownNavList,
  DropdownSize,
  Icon,
  Input,
  NavItem,
  NavListItemWithBlockDescription,
  useModal,
} from '@billy/design-system'
import { ChangeEvent, FocusEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from 'react-query'

import { useBankConnectionLimit } from '@features/openBanking/hooks/useBankConnectionLimit'
import { useFetchBanks } from '@features/openBanking/hooks/useFetchBanks'
import { useInitiateOpenBankingConnection } from '@features/openBanking/hooks/useInitiateOpenBankingConnection'
import type { UiSourceValue } from '@features/reconciliation/enums'
import { useCanConnectToShineBank } from '@features/shineBanking/hooks/useCanConnectToShineBank'
import { useInitiateShineConnection } from '@features/shineBanking/hooks/useInitiateShineConnection'
import { fetchCountries } from '@modules-deprecated/app/countries/query-api'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { ModalId } from '../../../../../../enums/modalId'
import { QueryKeys } from '../../../../../../enums/queryKeys'
import { TrackingContext } from '../../../../../../enums/trackingContext'
import { useComponentId, useSegment } from '../../../../../../hooks'
import { getCurrentLocale } from '../../../../../../utils'
import { BankValue, getBankItems, SHINE_BANK_ID } from '../../utils/getBankItems'

const CHAR_LENGTH_TO_OPEN_DROPDOWN = 2

interface BankSearchProps {
  dropdownSize?: DropdownSize
  onClear?: () => void
  prefilledSearch?: string
  uiSource?: UiSourceValue
}

export const BankSearch = ({ prefilledSearch, onClear, dropdownSize = 'fitTrigger', uiSource }: BankSearchProps) => {
  const [inputValue, setInputValue] = useState(prefilledSearch || '')
  const [isOpen, setIsOpen] = useState(false)
  const [isFocused, setIsFocused] = useState(false)
  const isDropdownInsideClickedRef = useRef(false)
  const { t } = useTranslation()
  const locale = getCurrentLocale()
  const { banks, isLoading } = useFetchBanks()
  const { connectToOpenBanking } = useInitiateOpenBankingConnection(uiSource)
  const { isLimitReached } = useBankConnectionLimit()
  const upgradeModalId = useComponentId(ModalId.UpgradeSubscriptionModal)
  const { open: openUpgradeModal } = useModal(upgradeModalId)
  const { initiateShineConnection } = useInitiateShineConnection()
  const { data: countryData } = useQuery([QueryKeys.Countries, locale], () => fetchCountries(locale))
  const { organization } = useUserOrganization()
  const { canConnectToShineBank } = useCanConnectToShineBank()
  const { track } = useSegment()

  const items: NavItem<BankValue>[] = useMemo(() => {
    if (inputValue.trim().length < CHAR_LENGTH_TO_OPEN_DROPDOWN) {
      return []
    }

    return getBankItems({
      banks,
      countries: countryData?.countries,
      currentOrgCountryId: organization?.countryId,
      searchQuery: inputValue,
      shouldSeeShineBank: canConnectToShineBank,
      t,
    })
  }, [inputValue, banks, countryData?.countries, organization?.countryId, canConnectToShineBank, t])

  const handleDropdownMouseDown = useCallback(() => {
    isDropdownInsideClickedRef.current = true
  }, [])

  const handleInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value

      setInputValue(value)
    },
    [setInputValue],
  )

  const handleItemClick = useCallback(
    (id: string) => {
      isDropdownInsideClickedRef.current = true
      if (isLimitReached) {
        openUpgradeModal()
        setIsFocused(false)
        setIsOpen(false)
        return
      }

      if (id === SHINE_BANK_ID) {
        initiateShineConnection()
      } else {
        connectToOpenBanking(id)
      }

      setIsFocused(false)
      setIsOpen(false)
      track('XXX - Reconciliation Bank Connection Initiated (Client)')
    },
    [connectToOpenBanking, track, initiateShineConnection, isLimitReached, openUpgradeModal],
  )

  const handleInputFocus = useCallback((event: FocusEvent<HTMLInputElement>) => {
    setIsFocused(true)
  }, [])

  const handleInputBlur = useCallback(() => {
    if (isDropdownInsideClickedRef.current) {
      isDropdownInsideClickedRef.current = false
    } else {
      setInputValue('')
      setIsOpen(false)
      onClear?.()
    }

    setIsFocused(false)
  }, [onClear])

  const handleClose = useCallback(() => {
    setIsOpen(false)
  }, [])

  useEffect(() => {
    const hasEnoughChars = inputValue.trim().length >= CHAR_LENGTH_TO_OPEN_DROPDOWN
    setIsOpen(hasEnoughChars)
  }, [inputValue])

  useEffect(() => {
    if (prefilledSearch) {
      setInputValue(prefilledSearch)
      setIsFocused(true)
    }
  }, [prefilledSearch])

  return (
    <>
      <DropdownNavList
        autoToggle={false}
        items={items}
        itemRender={(props) => <NavListItemWithBlockDescription {...props} />}
        isOpen={isOpen}
        size={dropdownSize}
        onItemClick={handleItemClick}
        onClose={handleClose}
        onMouseDown={handleDropdownMouseDown}
        inputSearchValue={inputValue}
        shouldSearchForBestMatched
        isFetching={isLoading}
        placement="bottom-start"
        trigger={
          <Input
            placeholder={t('bankreconciliation.connect_bank.search.placeholder')}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            suffix={<Icon icon="search" />}
            value={inputValue}
            focused={isFocused}
            onFocus={handleInputFocus}
          />
        }
      />
      <UpgradeSubscriptionModal id={upgradeModalId} trackingContext={TrackingContext.BankConnectionLimitsExceeded} />
    </>
  )
}
